import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import { StatusOptions } from "../components/StatusOptions";
import { Tag } from "../components/Tag";
import { ChangeSummary } from "../components/ChangeSummary";
import { changeJobStatus } from "../lib/jobStatus";
import { isNewJob, isRecentlyClosedJob } from "../lib/tags";
import { isWithinLastDays } from "../lib/dates";
import { classifyJobTitle, TITLE_GROUP_ORDER } from "../lib/jobTitleGroup";
import {
  STAGES,
  stageOf,
  PIPELINE_STATUSES,
  JOB_STATUSES,
  isRemoteLocation,
  isSeattleArea,
  type Company,
  type Job,
  type JobStatus,
} from "../lib/types";

type GroupBy = "title" | "company" | "stage";
type SortBy = "lastChanged" | "applicationDate" | "company" | "status";
type ChangeFilter = "all" | "new" | "updated";

const RECENT_DAYS = 7;

function sortComparator(sortBy: SortBy, companyName: (id: string) => string) {
  switch (sortBy) {
    case "applicationDate":
      return (a: Job, b: Job) => (b.application_date || "").localeCompare(a.application_date || "");
    case "company":
      return (a: Job, b: Job) => companyName(a.company_id).localeCompare(companyName(b.company_id));
    case "status":
      return (a: Job, b: Job) => JOB_STATUSES.indexOf(a.status) - JOB_STATUSES.indexOf(b.status);
    case "lastChanged":
    default:
      return (a: Job, b: Job) => (b.updated_at || "").localeCompare(a.updated_at || "");
  }
}

export function JobsPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const selectedCompanyId = params.get("company") ?? "";
  const [companies, setCompanies] = useState<Company[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("seattle");
  const [groupBy, setGroupBy] = useState<GroupBy>("title");
  const [sortBy, setSortBy] = useState<SortBy>("lastChanged");
  const [changeFilter, setChangeFilter] = useState<ChangeFilter>("all");
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    company_id: "",
    title: "",
    requisition: "",
    url: "",
    location: "",
    status: "Watching" as JobStatus,
    applied: false,
    application_date: "",
    notes: "",
  });

  const load = useCallback(async () => {
    if (!user) return;
    const [cRes, jRes] = await Promise.all([
      supabase.from("jh_companies").select("*").eq("user_id", user.id).order("name"),
      supabase.from("jh_jobs").select("*").eq("user_id", user.id),
    ]);
    if (cRes.error) setError(cRes.error.message);
    else setCompanies((cRes.data ?? []) as Company[]);
    if (jRes.error) setError(jRes.error.message);
    else setJobs((jRes.data ?? []) as Job[]);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const companyName = (id: string) => companies.find((c) => c.id === id)?.name ?? "(unknown)";

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      if (selectedCompanyId && j.company_id !== selectedCompanyId) return false;
      if (stageFilter !== "all" && stageOf(j.status) !== stageFilter) return false;
      if (statusFilter !== "all" && j.status !== statusFilter) return false;
      if (changeFilter === "new" && !isNewJob(j)) return false;
      if (changeFilter === "updated" && !isWithinLastDays(j.updated_at, RECENT_DAYS)) return false;
      const loc = j.location || "";
      if (locationFilter === "seattle" && !isSeattleArea(loc)) return false;
      if (locationFilter === "remote" && !isRemoteLocation(loc)) return false;
      if (
        locationFilter === "other" &&
        (isSeattleArea(loc) || isRemoteLocation(loc) || !loc.trim())
      )
        return false;
      const q = filter.trim().toLowerCase();
      if (!q) return true;
      return (
        j.title.toLowerCase().includes(q) ||
        companyName(j.company_id).toLowerCase().includes(q) ||
        loc.toLowerCase().includes(q) ||
        j.requisition.toLowerCase().includes(q) ||
        j.status.toLowerCase().includes(q)
      );
    });
  }, [jobs, companies, selectedCompanyId, stageFilter, statusFilter, changeFilter, locationFilter, filter]);

  /** Filtered jobs grouped by the chosen category, each group's jobs sorted by the chosen order. */
  const groups = useMemo(() => {
    const keyOf: (j: Job) => string =
      groupBy === "company"
        ? (j) => companyName(j.company_id)
        : groupBy === "stage"
          ? (j) => stageOf(j.status)
          : (j) => classifyJobTitle(j.title);

    const byKey = new Map<string, Job[]>();
    for (const j of filtered) {
      const key = keyOf(j);
      (byKey.get(key) ?? byKey.set(key, []).get(key)!).push(j);
    }

    const order =
      groupBy === "company"
        ? [...byKey.keys()].sort((a, b) => a.localeCompare(b))
        : groupBy === "stage"
          ? STAGES.filter((s) => byKey.has(s))
          : TITLE_GROUP_ORDER.filter((g) => byKey.has(g));
    for (const key of byKey.keys()) if (!order.includes(key)) order.push(key);

    const cmp = sortComparator(sortBy, companyName);
    return order.map((label) => ({ label, jobs: [...byKey.get(label)!].sort(cmp) }));
  }, [filtered, groupBy, sortBy, companies]);

  async function applyStatus(job: Job, status: JobStatus) {
    if (!user) return;
    const { patch, error: err } = await changeJobStatus(user.id, job, status);
    if (err) setError(err);
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, ...patch } : j)));
  }

  async function toggleApplied(job: Job, applied: boolean) {
    if (applied) {
      await applyStatus(job, job.status === "Watching" ? "Applied" : job.status);
      return;
    }
    const { error: err } = await supabase
      .from("jh_jobs")
      .update({ applied: false, updated_at: new Date().toISOString() })
      .eq("id", job.id);
    if (err) setError(err.message);
    else setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, applied: false } : j)));
  }

  async function addJob() {
    if (!user || !form.title.trim() || !(form.company_id || selectedCompanyId)) return;
    const company_id = form.company_id || selectedCompanyId;
    const external_id = `job_${Date.now()}`;
    const applied = form.applied || PIPELINE_STATUSES.includes(form.status);
    const { error: err } = await supabase.from("jh_jobs").insert({
      user_id: user.id,
      company_id,
      external_id,
      title: form.title.trim(),
      requisition: form.requisition.trim(),
      url: form.url.trim(),
      location: form.location.trim(),
      status: form.status,
      applied,
      application_date: form.application_date.trim(),
      notes: form.notes.trim(),
      last_seen: new Date().toISOString().slice(0, 10),
      closed_at: null,
    });
    if (err) setError(err.message);
    else {
      setShowForm(false);
      await load();
    }
  }

  async function removeJob(id: string) {
    if (!confirm("Remove this job?")) return;
    const { error: err } = await supabase.from("jh_jobs").delete().eq("id", id);
    if (err) setError(err.message);
    else await load();
  }

  return (
    <div className="panel">
      <ChangeSummary jobs={jobs} />
      <div className="toolbar">
        <input placeholder="Filter jobs…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}>
          <option value="all">All stages</option>
          {STAGES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <StatusOptions />
        </select>
        <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
          <option value="all">All locations</option>
          <option value="seattle">Seattle area</option>
          <option value="remote">Remote</option>
          <option value="other">Other</option>
        </select>
        <select value={changeFilter} onChange={(e) => setChangeFilter(e.target.value as ChangeFilter)}>
          <option value="all">All jobs</option>
          <option value="new">New jobs</option>
          <option value="updated">Updated jobs</option>
        </select>
        <label className="inline-select">
          Group by{" "}
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
            <option value="title">Job title</option>
            <option value="company">Company</option>
            <option value="stage">Application stage</option>
          </select>
        </label>
        <label className="inline-select">
          Sort by{" "}
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortBy)}>
            <option value="lastChanged">Last changed date</option>
            <option value="applicationDate">Application date</option>
            <option value="company">Company</option>
            <option value="status">Application status</option>
          </select>
        </label>
        {selectedCompanyId ? (
          <button
            type="button"
            className="btn"
            onClick={() => {
              params.delete("company");
              setParams(params);
            }}
          >
            Clear company filter ({companyName(selectedCompanyId)})
          </button>
        ) : null}
        <button type="button" className="btn primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Cancel" : "Add job"}
        </button>
      </div>
      {error ? <p className="error">{error}</p> : null}
      {showForm ? (
        <div className="stack-gap" style={{ marginBottom: 16 }}>
          <div className="field">
            <label>Company</label>
            <select
              value={form.company_id || selectedCompanyId}
              onChange={(e) => setForm({ ...form, company_id: e.target.value })}
            >
              <option value="">Select…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="field">
            <label>Requisition</label>
            <input
              value={form.requisition}
              onChange={(e) => setForm({ ...form, requisition: e.target.value })}
            />
          </div>
          <div className="field">
            <label>URL</label>
            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </div>
          <div className="field">
            <label>Location</label>
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as JobStatus })}
            >
              <StatusOptions />
            </select>
          </div>
          <label>
            <input
              type="checkbox"
              checked={form.applied}
              onChange={(e) => setForm({ ...form, applied: e.target.checked })}
            />{" "}
            Applied
          </label>
          <button type="button" className="btn primary" onClick={() => void addJob()}>
            Save job
          </button>
        </div>
      ) : null}

      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Company</th>
              <th>Title</th>
              <th>Location</th>
              <th>Req</th>
              <th>Stage</th>
              <th>Status</th>
              <th>Applied?</th>
              <th>App date</th>
              <th>Last change</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr>
                <td colSpan={10} className="muted">
                  No jobs match these filters.
                </td>
              </tr>
            ) : null}
            {groups.map((g) => (
              <Fragment key={g.label}>
                <tr className="group-row">
                  <td colSpan={10}>
                    <strong>{g.label}</strong> <span className="muted">({g.jobs.length})</span>
                  </td>
                </tr>
                {g.jobs.map((j) => (
                  <tr key={j.id}>
                    <td>
                      <button
                        type="button"
                        className="linkish"
                        onClick={() => setParams({ company: j.company_id })}
                      >
                        {companyName(j.company_id)}
                      </button>
                    </td>
                    <td>
                      {j.url ? (
                        <a href={j.url} target="_blank" rel="noreferrer">
                          {j.title}
                        </a>
                      ) : (
                        j.title
                      )}
                      {isNewJob(j) ? <Tag kind="new" /> : null}
                      {isRecentlyClosedJob(j) ? <Tag kind="closed" /> : null}
                    </td>
                    <td>{j.location || "—"}</td>
                    <td>{j.requisition || "—"}</td>
                    <td>{stageOf(j.status)}</td>
                    <td>
                      <select
                        value={j.status}
                        onChange={(e) => void applyStatus(j, e.target.value as JobStatus)}
                      >
                        <StatusOptions />
                      </select>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={j.applied}
                        onChange={(e) => void toggleApplied(j, e.target.checked)}
                      />
                    </td>
                    <td>{j.application_date || "—"}</td>
                    <td>{j.updated_at ? j.updated_at.slice(0, 10) : "—"}</td>
                    <td>
                      <button type="button" className="btn danger" onClick={() => void removeJob(j.id)}>
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
